<script setup>
import { computed } from 'vue';
import { achievementLevelDesc, expectedPerformance, schoolTypeLabel } from './data/reportLogic';

const props = defineProps({
  levelYear: { type: Number, default: 5 }, 
  userRole: { type: String, default: 'student' }, 
  studentLevel: { type: Number, default: 4 },
  schulType: { type: String, default: 'E' } // 'E' | 'M' | 'H' (소문자도 허용)
});

// schulType에 따른 학교 분류 표시
const schoolTypeText = computed(() => schoolTypeLabel(props.schulType));

const descriptionRows = computed(() => {
  return [4, 3, 2, 1].map(lv => ({
    level: lv,
    desc: achievementLevelDesc(lv)
  }));
});

const tableRows = computed(() => {
  if (props.userRole === 'principal') {
    return [4, 3, 2, 1].map(lv => ({ level: lv, desc: achievementLevelDesc(lv) }));
  }  
  if (props.userRole === 'teacher') {
    return [4, 3, 2, 1].map(lv => ({ 
      level: lv, 
      achievements: expectedPerformance(props.schulType, lv)
    }));
  }
  // student / parent: 학생 본인 수준 한 줄 (학부모용도 학교급별 문구를 그대로 사용)
  const lv = Number(props.studentLevel);
  return [{ 
    level: lv, 
    achievements: expectedPerformance(props.schulType, lv),
    desc: achievementLevelDesc(lv) 
  }];
});

const isSingleView = computed(() => props.userRole === 'student' || props.userRole === 'parent');
</script>
<template>
  <div class="card" :class="props.userRole">
    <template v-if="isSingleView"> 
      <h5 class="card-title mt-9">나의 수준에서 기대되는 성취 수행</h5>   
      <p class="msg-info pt-4">
        수준별 기대 성취 수행에 기술된 내용은 학생이 확률적으로 수행할 것으로 기대되는 내용을 제시한 것이며, 각 학생이 실제로 성취 수준에 있는 내용을 수행한다는 것을 의미하지는 않습니다.
      </p> 
    </template>
    <template v-else-if="props.userRole === 'teacher'">
      <h5 class="card-title">{{schoolTypeText}} {{props.levelYear}}학년 수준별 AI·디지털 리터러시 기대 성취 수행</h5>
      <p class="msg-info py-2">AI·디지털 리터러시 검사에서 성취수준은 4개의 수준으로 구성되는데, 4수준이 가장 높은 수준을 나타냅니다.</p>
    </template>
    <template v-else-if="props.userRole === 'principal'">
      <h5 class="card-title">성취 수준</h5>
      <p class="msg-info py-1">AI·디지털 리터러시 검사의 성취 수준별 상세 설명입니다.</p>
    </template>
    <div class="ai-table step-table primary" :class="{ 'mt-4 single-view': isSingleView }">
      <div class="row thead">
        <div class="th">수준</div>
        <div class="td" v-if="props.userRole === 'principal'">수준 설명</div>
        <div class="td" v-else>기대 성취 수행</div>      
      </div>
      <div class="row" v-for="row in tableRows" :key="row.level">
        <div class="th"><span class="step">{{ row.level }}수준</span></div>
        <div class="td">
          <template v-if="props.userRole === 'principal'">
            {{ row.desc }}
          </template>
          <template v-else>
            <p v-for="(achievement, index) in row.achievements" :key="index">
              {{ achievement }}
            </p>
          </template>
        </div>
      </div>
    </div> 
    <template v-if="isSingleView">
      <div class="pt-6 student-view">
        <h5 class="card-title">성취 수준</h5>
        <p class="msg-info py-2">
          AI·디지털 리터러시 검사에서 성취 수준은 4개의 수준으로 구성되는데, 4수준이 가장 높은 수준을 나타냅니다.
        </p>     
        <div class="ai-table primary">
          <div class="row thead">
            <div class="th">수준</div>
            <div class="td">수준 설명</div>
          </div>
          <div class="row" v-for="row in descriptionRows" :key="row.level">
            <div class="th">
              <span class="step">{{ row.level }}수준</span> 
            </div>
            <div class="td">
              <p>{{ row.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
    </div>
</template>

<style lang="scss" scoped>
@use "sass:list";
$ai-levels: (
  var(--result-ai-primary-90),
  var(--result-ai-primary-80),  
  var(--result-ai-primary-70),
  var(--result-ai-primary-60), 
);
.ai-table {  
  .row {
    > div {
      padding: 1rem 0 1rem 1rem;
      .step {
        font-weight: 600;         
        border-radius: 2.25rem;
        padding: .625rem 2rem;
        line-height: 1.6;
      }    
    }   
  } 
  @for $i from 2 through 5 {
    .row {
      &:nth-child(#{$i}) {
        .step {
          background-color: list.nth($ai-levels, $i - 1);        
        }
      }
    }    
  }   
  &.step-table {    
    .td {
      text-align: justify;
      word-break: keep-all;
      text-justify: inter-word;
      p {
        position: relative;
        padding-left: 15px;
        margin-bottom: .25rem;
        line-height: 1.3;
        &:last-child { margin-bottom: 0; }
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 11px;
          width: 3px;
          height: 3px;
          background-color: var(--result-ai-blak);
          border-radius: 50%;       
        }
      }
    }
    border-bottom: 0;
  } 
  &.single-view.step-table .row > div p { line-height: 1.7; }
}
// 학교장/학급용 4행 표(성취 수준 설명 · 기대 성취 수행) — 1rem 패딩 기본값이면 4개 수준 전체가 2p를 넘친다.
.card.principal .ai-table.step-table .row > div,
.card.teacher .ai-table.step-table .row > div {
  padding: 1.25rem 0 1.25rem 1rem;
}
.student-view .row > div {
  padding: .875rem 1.25rem;
  line-height: 1.4;
  &.td {
    text-align: justify;
    word-break: keep-all;
    text-justify: inter-word;
  }
}

.msg-info {
  text-align: justify;
  word-break: keep-all;
  text-justify: inter-word;
}
</style>